import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import '../../Styles/ManagementPages.css';
import '../../Styles/RouteManage.css';
import '../../Styles/PrimaryRouteManage.css';   // base "prm-" design
import '../../Styles/PrimaryBusOwner.css';      // owner / fleet / pagination additions (load LAST)
import PrimaryBusManagementModal from './PrimaryBusManagementModal';
import PrimaryBusFormModal from './PrimaryBusFormModal';
import {
  Icon, BusSvg, StatCard, Field, Avatar, Pagination, DAY_SHORT
} from './PrimaryUI';
import {
  STATIC_OWNERS, STATIC_PENDING_OWNERS, STATIC_DRIVERS, STATIC_CONDUCTORS
} from './primaryStaticData';

// ============================================================
// Owner add / edit form
// ============================================================
const OwnerFormModal = ({ initial, onClose, onSave }) => {
  const editing = !!initial;
  const [f, setF] = useState(initial || { name: '', company: '', email: '', phone: '' });
  const save = () => {
    if (!f.name.trim() || !f.company.trim() || !f.email.trim() || !f.phone.trim()) return toast.error('Please fill all fields');
    if (!/^\S+@\S+\.\S+$/.test(f.email)) return toast.error('Enter a valid email address');
    onSave({ ...f, name: f.name.trim(), company: f.company.trim(), email: f.email.trim(), phone: f.phone.trim() });
  };
  return (
    <div className="prm-overlay" onClick={onClose}>
      <div className="prm-form-modal prm-form-modal-sm" onClick={(e) => e.stopPropagation()}>
        <div className="prm-form-head">
          <div className="prm-form-head-row">
            <span className="prm-form-badge"><Icon name={editing ? 'edit' : 'building'} size={22} /></span>
            <div><h3>{editing ? 'Edit owner' : 'Add primary bus owner'}</h3><p>{editing ? initial.company : 'Created as pending until approved'}</p></div>
          </div>
          <button className="prm-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="prm-form-body">
          <div className="prm-fgrid">
            <Field label="Owner name" required><input className="prm-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Enter owner name" /></Field>
            <Field label="Company name" required><input className="prm-input" value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} placeholder="Enter company name" /></Field>
            <Field label="Email" required><input type="email" className="prm-input" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="name@company.com" /></Field>
            <Field label="Phone" required><input className="prm-input" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="10-digit mobile" /></Field>
          </div>
        </div>
        <div className="prm-form-foot">
          <span className="prm-foot-hint">Fields marked <em>*</em> are required</span>
          <div className="prm-form-foot-actions">
            <button className="prm-btn ghost" onClick={onClose}>Cancel</button>
            <button className="prm-btn primary" onClick={save}>{editing ? 'Save changes' : 'Add owner'}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// Owner card
// ============================================================
const OwnerCard = ({ owner, index, actions }) => (
  <div className={`prm-card pbo-owner-card ${owner.isApprovedByAdmin ? '' : 'pending'}`} style={{ animationDelay: `${index * 70}ms` }}>
    <div className="prm-card-top">
      <div className="pbo-owner-id">
        <Avatar name={owner.name} src={owner.profileImage} size={52} />
        <div>
          <div className="prm-card-no">Owner #{String(owner.serialNo).padStart(2, '0')}</div>
          <h3 className="prm-card-title">{owner.name}</h3>
          <div className="prm-card-sub"><Icon name="building" size={14} /> {owner.company || 'N/A'}</div>
        </div>
      </div>
      <span className={`prm-status ${owner.isApprovedByAdmin ? 'active' : 'inactive'}`}>{owner.isApprovedByAdmin ? 'Approved' : 'Pending'}</span>
    </div>
    <div className="pbo-contact">
      <div><Icon name="mail" size={14} /> {owner.email}</div>
      <div><Icon name="phone" size={14} /> {owner.phone}</div>
      {owner.city && <div><Icon name="pin" size={14} /> {owner.city}, {owner.state}</div>}
    </div>
    <div className="prm-metrics">
      <div className="prm-metric"><div className="v"><Icon name="bus" size={15} /> {owner.buses}</div><div className="l">Buses</div></div>
      <div className="prm-metric"><div className="v"><Icon name="check" size={15} /> {owner.status}</div><div className="l">Status</div></div>
      <div className="prm-metric"><div className="v"><Icon name="calendar" size={15} /> {owner.joined}</div><div className="l">Joined</div></div>
    </div>
    {owner.isPending && owner.submittedByName && <div className="prm-tag amber">Submitted by {owner.submittedByName}</div>}
    <div className="pbo-card-actions">{actions}</div>
  </div>
);

// ============================================================
// MAIN PAGE — static state, no API calls
// ============================================================
const PrimaryBusOwnerManage = () => {
  const [busOwners, setBusOwners] = useState([]);
  const [pendingOwners, setPendingOwners] = useState([]);
  const [staff, setStaff] = useState({ drivers: STATIC_DRIVERS, conductors: STATIC_CONDUCTORS });
  const [loading, setLoading] = useState(true);

  const [viewMode, setViewMode] = useState('regular');
  const [layout, setLayout] = useState('table');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);

  const [ownerForm, setOwnerForm] = useState(null);       // { mode:'add' } | { mode:'edit', owner }
  const [viewingOwner, setViewingOwner] = useState(null);
  const [busOwnerForAdd, setBusOwnerForAdd] = useState(null);
  const [managedOwnerId, setManagedOwnerId] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => {
      setBusOwners(STATIC_OWNERS.map(o => ({ ...o })));
      setPendingOwners(STATIC_PENDING_OWNERS.map(o => ({ ...o })));
      setLoading(false);
    }, 500);
    return () => clearTimeout(t);
  }, []);

  const currentOwners = viewMode === 'regular' ? busOwners : pendingOwners;

  const filteredOwners = useMemo(() => {
    const s = searchTerm.trim().toLowerCase();
    return currentOwners.filter(o => {
      if (statusFilter === 'approved' && !o.isApprovedByAdmin) return false;
      if (statusFilter === 'pending' && o.isApprovedByAdmin) return false;
      if (!s) return true;
      return [o.name, o.company, o.email].some(v => (v || '').toLowerCase().includes(s)) || (o.phone || '').includes(s);
    });
  }, [currentOwners, searchTerm, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredOwners.length / pageSize));
  // keep the page valid when data / filters / page size change
  useEffect(() => { if (currentPage > totalPages) setCurrentPage(totalPages); }, [currentPage, totalPages]);
  const paginatedOwners = filteredOwners.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const resetPaging = () => setCurrentPage(1);

  // stats
  const totalOwners = currentOwners.length;
  const verifiedOwners = currentOwners.filter(o => o.status === 'Verified').length;
  const approvedOwners = currentOwners.filter(o => o.isApprovedByAdmin).length;
  const totalBuses = currentOwners.reduce((s, o) => s + (o.buses || 0), 0);
  const approvedCount = currentOwners.filter(o => o.isApprovedByAdmin).length;

  const managedOwner = busOwners.find(o => o.id === managedOwnerId) || null;

  // ---------- handlers ----------
  const handleApprovePendingOwner = (id) => {
    const p = pendingOwners.find(o => o.id === id);
    if (!p) return;
    setPendingOwners(prev => prev.filter(o => o.id !== id));
    setBusOwners(prev => [{ ...p, isPending: false, serialNo: prev.length + 1, isVerified: true, isApprovedByAdmin: true, status: 'Verified', adminApproval: 'Approved' }, ...prev]);
    toast.success('Pending owner approved');
  };
  const handleRejectPendingOwner = (id) => {
    if (!window.confirm('Reject this pending owner?')) return;
    setPendingOwners(prev => prev.filter(o => o.id !== id));
    toast.success('Owner rejected');
  };
  const handleApproveOwner = (id) => {
    setBusOwners(prev => prev.map(o => o.id === id ? { ...o, isApprovedByAdmin: true, adminApproval: 'Approved' } : o));
    toast.success('Owner approved');
  };
  const handleRejectOwner = (id) => {
    if (!window.confirm('Reject this owner?')) return;
    setBusOwners(prev => prev.map(o => o.id === id ? { ...o, isApprovedByAdmin: false, adminApproval: 'Rejected' } : o));
    toast.success('Owner rejected');
  };
  const handleDeleteOwner = (id) => {
    if (!window.confirm('Delete this owner?')) return;
    (viewMode === 'regular' ? setBusOwners : setPendingOwners)(prev => prev.filter(o => o.id !== id));
    toast.success('Owner deleted');
  };

  const handleSaveOwner = (data) => {
    if (ownerForm.mode === 'edit') {
      const id = ownerForm.owner.id;
      (viewMode === 'regular' ? setBusOwners : setPendingOwners)(prev => prev.map(o => o.id === id ? { ...o, ...data } : o));
      toast.success('Owner updated');
    } else {
      const id = 'ow_' + Date.now();
      setBusOwners(prev => [{
        id, serialNo: prev.length + 1, profileImage: '', ...data, buses: 0, routes: 0,
        joined: new Date().toISOString().split('T')[0], status: 'Pending', adminApproval: 'Pending',
        isVerified: false, isApprovedByAdmin: false, busAll: []
      }, ...prev]);
      setStaff(s => ({ drivers: { ...s.drivers, [id]: [] }, conductors: { ...s.conductors, [id]: [] } }));
      setViewMode('regular');
      resetPaging();
      toast.success('Owner added');
    }
    setOwnerForm(null);
  };

  const handleManageBuses = (owner) => {
    if (!owner.busAll?.length) return toast.info('This owner has no primary buses yet.');
    setManagedOwnerId(owner.id);
  };

  const handleBusAdded = (newBus) => {
    setBusOwners(prev => prev.map(o => o.id === busOwnerForAdd.id
      ? { ...o, buses: (o.buses || 0) + 1, busAll: [...(o.busAll || []), newBus] } : o));
  };

  const handleBusesChange = (ownerId, buses) => {
    setBusOwners(prev => prev.map(o => o.id === ownerId ? { ...o, busAll: buses, buses: buses.length } : o));
  };

  const handleStaffChange = (ownerId, { drivers, conductors }) => {
    setStaff(s => ({ drivers: { ...s.drivers, [ownerId]: drivers }, conductors: { ...s.conductors, [ownerId]: conductors } }));
  };

  const handleRefresh = () => {
    setSearchTerm(''); setStatusFilter('all'); resetPaging();
    toast.info('Filters cleared');
  };

  // ---------- action buttons (shared by table + cards) ----------
  const renderActions = (owner) => (
    <>
      <button className="prm-btn ghost sm" onClick={() => setViewingOwner(owner)}><Icon name="eye" size={14} /> View</button>
      <button className="prm-btn ghost sm" onClick={() => setOwnerForm({ mode: 'edit', owner })}><Icon name="edit" size={14} /> Edit</button>
      {!owner.isPending ? (
        <>
          <button className="prm-btn view sm" onClick={() => setBusOwnerForAdd(owner)}><Icon name="plus" size={14} /> Bus</button>
          <button className="prm-btn teal sm" onClick={() => handleManageBuses(owner)}><Icon name="bus" size={14} /> Manage</button>
          {owner.isApprovedByAdmin
            ? <button className="prm-btn warn sm" onClick={() => handleRejectOwner(owner.id)}>Reject</button>
            : <button className="prm-btn ok sm" onClick={() => handleApproveOwner(owner.id)}>Approve</button>}
          <button className="prm-btn danger sm" onClick={() => handleDeleteOwner(owner.id)} title="Delete"><Icon name="trash" size={14} /></button>
        </>
      ) : (
        <>
          <button className="prm-btn ok sm" onClick={() => handleApprovePendingOwner(owner.id)}>Approve</button>
          <button className="prm-btn danger sm" onClick={() => handleRejectPendingOwner(owner.id)}>Reject</button>
        </>
      )}
    </>
  );

  // ============================================================
  if (loading) {
    return (
      <div className="prm-page">
        <div className="prm-hero"><div className="prm-hero-text">
          <span className="prm-eyebrow"><Icon name="bus" size={13} /> Primary Fleet</span>
          <h1>Primary Bus Owner Management</h1><p>Loading owners…</p>
        </div></div>
        <div style={{ height: 24 }} />
        <div className="prm-grid">{[0, 1, 2].map(i => <div className="prm-skel" key={i}><div className="w40" /><div className="w60" /><div className="big" /><div /><div className="w60" /></div>)}</div>
      </div>
    );
  }

  return (
    <div className="prm-page">
      {/* HERO */}
      <div className="prm-hero">
        <div className="prm-hero-road" aria-hidden="true"><span className="prm-hero-roadbus"><BusSvg size={46} /></span></div>
        <div className="prm-hero-text">
          <span className="prm-eyebrow"><Icon name="users" size={13} /> Primary Fleet Owners</span>
          <h1>Primary Bus Owner Management</h1>
          <p>Approve owners, register their weekly primary buses and assign drivers and conductors.</p>
        </div>
        <div className="prm-hero-actions">
          <div className="prm-search">
            <Icon name="search" size={17} />
            <input type="text" placeholder="Search name, company, email or phone…" value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); resetPaging(); }} />
            {searchTerm && <button className="prm-search-clear" onClick={() => { setSearchTerm(''); resetPaging(); }}>×</button>}
          </div>
          <button className="pbo-icon-btn" onClick={handleRefresh} title="Clear filters"><Icon name="refresh" size={17} /></button>
          {viewMode === 'regular' && (
            <button className="prm-add-btn" onClick={() => setOwnerForm({ mode: 'add' })}>
              <span className="plus"><Icon name="plus" size={14} /></span> Add Owner
            </button>
          )}
        </div>
      </div>

      {/* STATS */}
      <div className="prm-stats">
        <StatCard icon="users" tone="indigo" label={viewMode === 'regular' ? 'Total Owners' : 'Pending'} value={totalOwners} hint={viewMode === 'regular' ? 'Registered' : 'Awaiting approval'} delay={80} />
        <StatCard icon="check" tone="green" label="Verified" value={verifiedOwners} hint="Verified owners" delay={160} />
        <StatCard icon="tick" tone="amber" label="Approved" value={approvedOwners} hint="Admin approved" delay={240} />
        <StatCard icon="bus" tone="cyan" label="Total Buses" value={totalBuses} hint="Primary fleet" delay={320} />
      </div>

      {/* TOOLBAR */}
      <div className="prm-toolbar">
        <div className="prm-toolbar-left">
          <div className="prm-segment">
            <span className="prm-segment-pill" style={{ transform: `translateX(${viewMode === 'regular' ? 0 : 100}%)` }} />
            <button className={viewMode === 'regular' ? 'active' : ''} onClick={() => { setViewMode('regular'); resetPaging(); setSearchTerm(''); setStatusFilter('all'); }}><Icon name="user" size={15} /> Regular</button>
            <button className={viewMode === 'pending' ? 'active' : ''} onClick={() => { setViewMode('pending'); resetPaging(); setSearchTerm(''); setStatusFilter('all'); }}>
              <Icon name="clipboard" size={15} /> Executive Submitted
              {pendingOwners.length > 0 && <span className="pbo-badge">{pendingOwners.length}</span>}
            </button>
          </div>
          <div className="prm-chips">
            {[['all', 'All', currentOwners.length], ['approved', 'Approved', approvedCount], ['pending', 'Pending', currentOwners.length - approvedCount]].map(([k, l, c]) => (
              <button key={k} className={`prm-chip ${statusFilter === k ? 'active' : ''}`} onClick={() => { setStatusFilter(k); resetPaging(); }}>
                {l} <span className="count">{c}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="prm-toolbar-right">
          <span className="prm-result-count">Showing <b>{filteredOwners.length}</b> of {currentOwners.length}</span>
          <div className="prm-layout-toggle">
            <button className={layout === 'cards' ? 'active' : ''} onClick={() => setLayout('cards')} title="Card view"><Icon name="grid" size={17} /></button>
            <button className={layout === 'table' ? 'active' : ''} onClick={() => setLayout('table')} title="Table view"><Icon name="list" size={17} /></button>
          </div>
        </div>
      </div>

      {/* LIST */}
      {filteredOwners.length === 0 ? (
        <div className="prm-empty">
          <div className="icon"><Icon name={searchTerm || statusFilter !== 'all' ? 'search' : 'users'} size={44} /></div>
          <h3>{searchTerm || statusFilter !== 'all' ? 'No owners match your filters' : 'No owners yet'}</h3>
          <p>{searchTerm || statusFilter !== 'all' ? 'Try a different search or clear the filters.' : 'Add your first primary bus owner to get started.'}</p>
          {(searchTerm || statusFilter !== 'all') && <button className="prm-btn ghost" onClick={handleRefresh}>Clear filters</button>}
        </div>
      ) : layout === 'cards' ? (
        <div className="prm-grid">
          {paginatedOwners.map((o, i) => <OwnerCard key={o.id} owner={o} index={i} actions={renderActions(o)} />)}
        </div>
      ) : (
        <div className="prm-table-wrap">
          <table className="prm-table">
            <thead>
              <tr><th>No.</th><th>Owner</th><th>Company</th><th>Contact</th><th>Buses</th><th>Approval</th><th>Status</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {paginatedOwners.map((o, i) => (
                <tr key={o.id} style={{ animationDelay: `${i * 60}ms` }} className={o.isPending ? 'pbo-row-pending' : ''}>
                  <td>{o.serialNo}</td>
                  <td>
                    <div className="pbo-cell-owner">
                      <Avatar name={o.name} src={o.profileImage} />
                      <div>
                        <div className="name">{o.name}{o.isPending && <span className="prm-tag amber" style={{ marginLeft: 8 }}>Pending</span>}</div>
                        <div className="sub">{o.city ? `${o.city}, ${o.state}` : '—'}</div>
                      </div>
                    </div>
                  </td>
                  <td>{o.company || 'N/A'}</td>
                  <td><div className="name">{o.email}</div><div className="sub">{o.phone}</div></td>
                  <td><span className="pbo-count">{o.buses}</span></td>
                  <td><span className={`prm-status ${o.isApprovedByAdmin ? 'active' : 'inactive'}`}>{o.isApprovedByAdmin ? 'Approved' : 'Pending'}</span></td>
                  <td><span className={`prm-status ${o.status === 'Verified' ? 'active' : 'inactive'}`}>{o.status || 'Pending'}</span></td>
                  <td><div className="prm-row-actions pbo-wrap">{renderActions(o)}</div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* PAGINATION — always shown, in both Regular and Executive Submitted */}
      <Pagination
        page={currentPage} pageSize={pageSize} total={filteredOwners.length}
        onPage={(p) => setCurrentPage(Math.min(Math.max(1, p), totalPages))}
        onPageSize={(s) => { setPageSize(s); setCurrentPage(1); }}
        label="primary owners"
      />

      {/* MODALS */}
      {ownerForm && (
        <OwnerFormModal
          initial={ownerForm.mode === 'edit' ? { name: ownerForm.owner.name, company: ownerForm.owner.company, email: ownerForm.owner.email, phone: ownerForm.owner.phone } : null}
          onClose={() => setOwnerForm(null)}
          onSave={handleSaveOwner} />
      )}

      {busOwnerForAdd && (
        <PrimaryBusFormModal owner={busOwnerForAdd} onClose={() => setBusOwnerForAdd(null)} onSave={handleBusAdded} />
      )}

      {viewingOwner && (
        <div className="prm-overlay" onClick={() => setViewingOwner(null)}>
          <div className="prm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="prm-modal-head">
              <h3>{viewingOwner.name}</h3>
              <p><Icon name="building" size={14} /> {viewingOwner.company}</p>
              <button className="prm-modal-close" onClick={() => setViewingOwner(null)}>×</button>
            </div>
            <div className="prm-modal-body">
              <div className="prm-info-grid">
                <div className="prm-info"><div className="l">Buses</div><div className="v"><Icon name="bus" size={17} /> {viewingOwner.buses}</div></div>
                <div className="prm-info"><div className="l">Status</div><div className="v"><Icon name="check" size={17} /> {viewingOwner.status}</div></div>
                <div className="prm-info"><div className="l">Joined</div><div className="v"><Icon name="calendar" size={17} /> {viewingOwner.joined}</div></div>
              </div>
              <div className="prm-section-title">Contact</div>
              <div className="pbo-contact big">
                <div><Icon name="mail" size={15} /> {viewingOwner.email}</div>
                <div><Icon name="phone" size={15} /> {viewingOwner.phone}</div>
                <div><Icon name="pin" size={15} /> {viewingOwner.city}, {viewingOwner.state}</div>
              </div>
              {viewingOwner.busAll?.length > 0 && (
                <>
                  <div className="prm-section-title" style={{ marginTop: 22 }}>Primary buses</div>
                  <div className="pbo-view-buses">
                    {viewingOwner.busAll.map(b => (
                      <div key={b._id} className="pbo-view-bus">
                        <div><b>{b.busName}</b><small>{b.busNumber} · {b.busType}</small></div>
                        <div className="prm-mini-days">{(b.departureDays || []).map(d => <span key={d}>{DAY_SHORT[d]}</span>)}</div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
            <div className="prm-modal-foot">
              <button className="prm-btn view" style={{ flex: 'none', padding: '11px 28px' }} onClick={() => setViewingOwner(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {managedOwner && (
        <PrimaryBusManagementModal
          owner={managedOwner}
          buses={managedOwner.busAll || []}
          drivers={staff.drivers[managedOwner.id] || []}
          conductors={staff.conductors[managedOwner.id] || []}
          onClose={() => setManagedOwnerId(null)}
          onBusesChange={(b) => handleBusesChange(managedOwner.id, b)}
          onStaffChange={(s) => handleStaffChange(managedOwner.id, s)}
        />
      )}
    </div>
  );
};

export default PrimaryBusOwnerManage;